import { useCustomController } from "components/Reusable/Form/useCustomController";
import { useContent } from "contexts/contentContext/contentContext";
import { EMAIL_VALIDATOR, PHONE_VALIDATOR, urlRegex } from "helpers/validators";
import { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookF,
  faLinkedinIn,
  faSquareInstagram,
  faSquareWhatsapp,
  faVimeoV,
  faXTwitter,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import {
  faEnvelope,
  faGlobe,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

interface BusinessProfileFieldI {
  item: {
    type: string;
  };
  label: string;
  icon: IconProp;
}

const TextDivider = ({ textValue }: { textValue: string }) => {
  const { getTextByKey } = useContent();
  return (
    <div className="bussiness-profile-section-header">
      <h3>{getTextByKey(textValue)}</h3>
    </div>
  );
};

export const BusinessProfileField = (props: BusinessProfileFieldI) => {
  const { fieldControls, hasError } = useCustomController(props);
  const { getTextByKey } = useContent();
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState("");

  useEffect(() => {
    setValue(fieldControls.value);
  }, []);

  const handleChangeValue = (e) => {
    setValue(e.target.value);
  };

  useEffect(() => {
    fieldControls.onChange(value);
  }, [value]);

  return (
    <div
      className={`input-container ${isFocused ? "focus-item" : ""} ${
        hasError ? "invalid-item" : ""
      }`}
    >
      <label>{getTextByKey(props.label)}</label>
      <div className="input-div">
        <div className="input-image">
          <FontAwesomeIcon icon={props.icon} className="input-icon" />
        </div>
        <input
          value={value}
          onChange={handleChangeValue}
          onBlur={(e) => setIsFocused(false)}
          onFocus={(e) => setIsFocused(true)}
          type={props.item.type || "text"}
        />
      </div>
    </div>
  );
};

const businessProfileFields = {
  FirstDivider: {
    element: "customelement",
    containerClassName: "input-full-width",
    render: () => <TextDivider textValue="business-profile-social-links" />,
  },
  FacebookLink: {
    element: "customelement",
    fieldKey: "FacebookLink",
    validation: {
      required: false,
      invalidMessage: "facebook-error-message",
      reg: urlRegex,
    },
    render: (props) => (
      <BusinessProfileField {...props} label="facebook" icon={faFacebookF} />
    ),
  },
  InstagramLink: {
    element: "customelement",
    fieldKey: "InstagramLink",
    validation: {
      required: false,
      invalidMessage: "instagram-error-message",
      reg: urlRegex,
    },
    render: (props) => (
      <BusinessProfileField
        {...props}
        label="instagram"
        icon={faSquareInstagram}
      />
    ),
  },
  TwitterLink: {
    element: "customelement",
    fieldKey: "TwitterLink",
    validation: {
      required: false,
      invalidMessage: "twitter-error-message",
      reg: urlRegex,
    },
    render: (props) => (
      <BusinessProfileField {...props} label="twitter" icon={faXTwitter} />
    ),
  },
  YoutubeLink: {
    element: "customelement",
    fieldKey: "YoutubeLink",
    validation: {
      required: false,
      invalidMessage: "youtube-error-message",
      reg: urlRegex,
    },
    render: (props) => (
      <BusinessProfileField {...props} label="youtube" icon={faYoutube} />
    ),
  },
  VimeoLink: {
    element: "customelement",
    fieldKey: "VimeoLink",
    validation: {
      required: false,
      invalidMessage: "vimeo-error-message",
      reg: urlRegex,
    },
    render: (props) => (
      <BusinessProfileField {...props} label="vimeo" icon={faVimeoV} />
    ),
  },
  LinkedinLink: {
    element: "customelement",
    fieldKey: "LinkedinLink",
    validation: {
      required: false,
      invalidMessage: "linkedin-error-message",
      reg: urlRegex,
    },
    render: (props) => (
      <BusinessProfileField {...props} label="linkedin" icon={faLinkedinIn} />
    ),
  },
  WhatsappLink: {
    element: "customelement",
    fieldKey: "WhatsappLink",
    validation: {
      required: false,
      invalidMessage: "whatsapp-error-message",
      reg: urlRegex,
    },
    render: (props) => (
      <BusinessProfileField
        {...props}
        label="whatsapp"
        icon={faSquareWhatsapp}
      />
    ),
  },
  WebsiteLink: {
    element: "customelement",
    fieldKey: "WebsiteLink",
    validation: {
      required: false,
      invalidMessage: "website-error-message",
      reg: urlRegex,
    },
    render: (props) => (
      <BusinessProfileField {...props} label="website" icon={faGlobe} />
    ),
  },
  SecondDivider: {
    element: "customelement",
    containerClassName: "input-full-width",
    render: () => <TextDivider textValue="business-profile-contact-details" />,
  },
  ContactByPhone: {
    element: "customelement",
    fieldKey: "ContactByPhone",
    type: "tel",
    validation: {
      required: false,
      invalidMessage: "phone-error-message",
      reg: PHONE_VALIDATOR,
    },
    render: (props) => (
      <BusinessProfileField {...props} label="phone" icon={faPhone} />
    ),
  },
  ContactByEmail: {
    element: "customelement",
    fieldKey: "ContactByEmail",
    type: "email",
    validation: {
      required: false,
      invalidMessage: "invalid-email-text",
      reg: EMAIL_VALIDATOR,
    },
    render: (props) => (
      <BusinessProfileField
        {...props}
        label="signin-email-label"
        icon={faEnvelope}
      />
    ),
  },
};

export default businessProfileFields;
